import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { LOGIN_MUTATION, REGISTER_MUTATION, ME_QUERY, LOGOUT_MUTATION, REFRESH_TOKEN_MUTATION } from '../services/authQueries';
import type { User, LoginInput, RegisterInput } from '../services/authQueries';
import { Permission } from '../types';
// Rimuoviamo gli import vecchi, usiamo quelli GraphQL

// Auth State
interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  permissions: Permission[];
}

// Auth Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' };

// Initial State
const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
  permissions: [],
};

// Auth Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
        permissions: (action.payload.roleId?.permissions && Array.isArray(action.payload.roleId.permissions) && action.payload.roleId.permissions.length > 0 && typeof action.payload.roleId.permissions[0] === 'object')
          ? action.payload.roleId.permissions as Permission[]
          : [],
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        loading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
        permissions: [],
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
        error: null,
        permissions: (action.payload.roleId?.permissions && Array.isArray(action.payload.roleId.permissions) && action.payload.roleId.permissions.length > 0 && typeof action.payload.roleId.permissions[0] === 'object')
          ? action.payload.roleId.permissions as Permission[]
          : state.permissions,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Auth Context Type
interface AuthContextType {
  user: User | null;
  loading: boolean;
  permissions: Permission[];
  hasPermission: (permissionName: string) => boolean;
  hasAnyPermission: (permissionNames: string[]) => boolean;
  hasAllPermissions: (permissionNames: string[]) => boolean;
  login: (credentials: LoginInput) => Promise<void>;
  /** Sessione ottenuta da un link diretto al ticket (Task 27): salva i token come il login e carica l'utente. */
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  register: (userData: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  /** Ri-legge l'utente corrente (ME_QUERY) e aggiorna il contesto: serve dopo
   *  operazioni REST che cambiano il profilo (es. foto) senza passare da Apollo. */
  refreshUser: () => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
  clearError: () => void;
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // GraphQL Mutations
  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [registerMutation] = useMutation(REGISTER_MUTATION);
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);
  const [refreshTokenMutation] = useMutation(REFRESH_TOKEN_MUTATION);
  const apolloClient = useApolloClient();

  // GraphQL Query per utente corrente
  const { refetch: refetchMe } = useQuery(ME_QUERY, {
    skip: !localStorage.getItem('accessToken'),
    errorPolicy: 'ignore'
  });

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('accessToken');

      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          const result = await refetchMe();
          if (result.data?.me) {
            dispatch({ type: 'AUTH_SUCCESS', payload: result.data.me });
          } else {
            throw new Error('No user data');
          }
        } catch (error) {
          // Token might be expired, clear tokens and logout
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
  }, [refetchMe]);

  // Login function
  const login = async (credentials: LoginInput): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const result = await loginMutation({
        variables: { input: credentials }
      });

      if (result.data?.login) {
        const { user, accessToken, refreshToken } = result.data.login;

        // Store tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Sessione da link diretto al ticket (Task 27): stessi token del login, utente riletto con ME_QUERY.
  const loginWithTokens = async (accessToken: string, refreshToken: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      const { data } = await apolloClient.query({ query: ME_QUERY, fetchPolicy: 'network-only' });
      if (!data?.me) throw new Error('No user data');
      dispatch({ type: 'AUTH_SUCCESS', payload: data.me });
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Register function
  const register = async (data: RegisterInput): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const result = await registerMutation({
        variables: { input: data }
      });

      if (result.data?.register) {
        const { user, accessToken, refreshToken } = result.data.register;

        // Store tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (refreshTokenValue) {
        await logoutMutation({
          variables: { refreshToken: refreshTokenValue }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call result
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const result = await refreshTokenMutation({
        variables: { refreshToken: refreshTokenValue }
      });

      if (result.data?.refreshToken) {
        const { accessToken, refreshToken: newRefreshToken } = result.data.refreshToken;

        // Update tokens in localStorage
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Refetch user data with new token
        const { data: userData } = await refetchMe();
        if (userData?.me) {
          dispatch({ type: 'UPDATE_USER', payload: userData.me });
        }
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Refresh token error:', error);
      // If refresh fails, logout user
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      dispatch({ type: 'AUTH_LOGOUT' });
      throw error;
    }
  };

  // Ricarica l'utente corrente dal backend (usata dopo upload/rimozione foto)
  const refreshUser = async (): Promise<void> => {
    try {
      const { data } = await refetchMe();
      if (data?.me) dispatch({ type: 'UPDATE_USER', payload: data.me });
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  // Update profile function (placeholder - implementeremo dopo)
  const updateProfile = async (data: any): Promise<void> => {
    // TODO: Implementare update profile
    throw new Error('Update profile not implemented yet');
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Permission helper functions
  const hasPermission = (permissionName: string): boolean => {
    if (!state.user) return false;

    // Admin ha sempre tutti i permessi (verifica tramite RBAC)
    if (state.user.roleId?.name === 'amministratore' || state.user.roleId?.name === 'admin') return true;

    // Verifica nel sistema RBAC
    return state.permissions.some(p => p.name === permissionName);
  };

  const hasAnyPermission = (permissionNames: string[]): boolean => {
    if (!state.user) return false;

    // Admin ha sempre tutti i permessi
    if (state.user.roleId?.name === 'amministratore' || state.user.roleId?.name === 'admin') return true;

    return permissionNames.some(name =>
      state.permissions.some(p => p.name === name)
    );
  };

  const hasAllPermissions = (permissionNames: string[]): boolean => {
    if (!state.user) return false;

    // Admin ha sempre tutti i permessi
    if (state.user.roleId?.name === 'amministratore' || state.user.roleId?.name === 'admin') return true;

    return permissionNames.every(name =>
      state.permissions.some(p => p.name === name)
    );
  };

  // Context value
  const value: AuthContextType = {
    user: state.user,
    loading: state.loading,
    permissions: state.permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    loginWithTokens,
    register,
    logout,
    refreshToken,
    refreshUser,
    updateProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export context for advanced usage
export { AuthContext };
